import { JsonSchema7 } from '@jsonforms/core';
import { DashboardWidgetDefinition } from 'src/app/shared/dashboard-grid';
import { DashboardFilters } from '../../dashboard.state';
import { AlertsSentPriority, Metric, MetricFilter, MetricId, MetricOwnSettings, METRICS } from './metrics';

/** A metric folded into Alerts Sent fixed to Emergency; tiles saved before still store it */
const EMERGENCY_ALERTS_SENT = 'emergencyAlertsSent';

/** The tile's metric, with that metric's own settings beside it */
export interface MetricSettings {
  metric: MetricId | typeof EMERGENCY_ALERTS_SENT;
  [setting: string]: unknown;
}

const EMERGENCY_ALERTS_SENT_TILE: MetricSettings = {
  metric: 'alertsSent',
  priority: 'emergency' satisfies AlertsSentPriority,
};

export interface TileMetric {
  id: MetricId;
  metric: Metric;
  /** The metric's own settings, over its defaults */
  settings: MetricOwnSettings;
}

/** What a tile shows, whatever version of the settings it stored */
export function tileMetricOf(tile: MetricSettings): TileMetric {
  const { metric: id, ...stored } = tile.metric === EMERGENCY_ALERTS_SENT ? EMERGENCY_ALERTS_SENT_TILE : tile;
  const metric = METRICS[id as MetricId];
  return { id: id as MetricId, metric, settings: { ...metric.settings?.defaults, ...stored } };
}

export function metricFiltersOf({ metric, settings }: TileMetric): readonly MetricFilter[] {
  return metric.settings?.filtersFor(settings) ?? metric.filters;
}

export function metricLabelOf({ metric, settings }: TileMetric): string {
  return metric.settings?.labelFor?.(settings) ?? metric.label;
}

/** Each metric's own settings apply only while it is the tile's metric */
function metricSettingsSchema(): JsonSchema7 {
  const ids = Object.keys(METRICS) as MetricId[];
  return {
    type: 'object',
    properties: { metric: { type: 'string', enum: [...ids, EMERGENCY_ALERTS_SENT] } },
    required: ['metric'],
    allOf: ids.flatMap((id) => {
      const schema = METRICS[id].settings?.schema;
      return schema ? [{ if: { properties: { metric: { const: id } } }, then: schema }] : [];
    }),
  };
}

/** One KPI tile; each instance's settings choose its metric. */
export const METRIC_WIDGET: DashboardWidgetDefinition<MetricSettings, DashboardFilters> = {
  type: 'metric',
  title: 'Dashboard.Metric.Title',
  description: 'A single number, such as open incidents or alerts sent. Add one tile per metric.',
  icon: 'speed',
  loadComponent: () => import('./metric-widget.component').then((m) => m.MetricWidgetComponent),
  header: false,
  size: { default: { w: 2, h: 2 }, min: { w: 2, h: 2 }, max: { w: 4, h: 2 } },
  defaultSettings: { metric: 'alertsSent' },
  settingsSchema: metricSettingsSchema(),
  settingsPermissions: (tile) => tileMetricOf(tile).metric.permissions ?? [],
  filtersFor: (tile) => metricFiltersOf(tileMetricOf(tile)),
  loadSettingsComponent: () => import('./metric-settings.component').then((m) => m.MetricSettingsComponent),
  maxInstances: Infinity,
};
